interface BuildModalPrintDocumentParams {
  title: string;
  headerHtml: string;
  bodyHtml: string;
  styles: string;
  compact?: boolean;
  repeatHeaderOnEveryPage?: boolean;
}

export const PRINT_HEADER_ID = 'htm-print-header';
export const PRINT_BODY_ID = 'htm-print-body';

const MM_TO_PX = 96 / 25.4;
const A4_PORTRAIT_WIDTH_MM = 210;
const A4_PORTRAIT_HEIGHT_MM = 297;
const PRINT_PAGE_MARGIN_MM = 15;
const PRINT_PAGE_CONTENT_WIDTH_MM = A4_PORTRAIT_WIDTH_MM - PRINT_PAGE_MARGIN_MM * 2;
const PRINT_PAGE_CONTENT_HEIGHT_PX = Math.floor(
  (A4_PORTRAIT_HEIGHT_MM - PRINT_PAGE_MARGIN_MM * 2) * MM_TO_PX
);

/**
 * Chromium reprints a `table-header-group` on every page only while the group fits in
 * a quarter of the page box — 252px of A4's 1009px content height. One pixel over and
 * it silently gives up and prints the header once, on page one, with no warning and no
 * fallback. Anything that grows the repeated header has to stay under this.
 */
export const PRINT_REPEATED_HEADER_MAX_HEIGHT_PX = Math.floor(PRINT_PAGE_CONTENT_HEIGHT_PX / 4);

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const COMPACT_PRINT_STYLE = `
  <style>
    #document-info-header {
      margin-bottom: 0.75rem !important;
    }
    #document-info-header td {
      padding: 0.25rem 0.5rem !important;
    }
    #document-info-header td[rowspan] {
      width: 8rem !important;
    }
    #document-info-header .text-lg {
      font-size: 0.8125rem !important;
      line-height: 1.125rem !important;
    }
    #document-info-header .text-sm {
      font-size: 0.6875rem !important;
      line-height: 0.875rem !important;
    }
    #document-info-header svg,
    #document-info-header img {
      width: 5rem !important;
      height: 2.5rem !important;
    }
    #${PRINT_BODY_ID} .mb-6 {
      margin-bottom: 0.5rem !important;
    }
    #${PRINT_BODY_ID} .py-6 {
      padding-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
    }
    #${PRINT_BODY_ID} .mt-8 {
      margin-top: 0.5rem !important;
    }
    #${PRINT_BODY_ID} .p-5 {
      padding: 0.375rem 0.75rem !important;
    }
    #${PRINT_BODY_ID} .h-12 {
      height: 2.25rem !important;
    }
  </style>
`;

/**
 * The header is lifted straight out of the on-screen card, where it is sized for a
 * screen: a 152px logo, 24px of card padding on each side and the info table's own
 * bottom margin add up to a 260px group — 8px past
 * `PRINT_REPEATED_HEADER_MAX_HEIGHT_PX`, which is enough to stop it repeating at all.
 * Printing the header at the size a sheet of paper actually needs brings the group to
 * roughly half the budget, so it survives a second line in the title or a taller logo
 * instead of silently falling back to page one only.
 */
export const REPEATED_PRINT_HEADER_STYLE = `
  <style>
    #${PRINT_HEADER_ID} > * {
      padding-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
    }
    #${PRINT_HEADER_ID} #document-info-header {
      margin-bottom: 0 !important;
    }
    #${PRINT_HEADER_ID} #document-info-header td {
      padding: 0.375rem 0.5rem !important;
    }
    #${PRINT_HEADER_ID} #document-info-header svg,
    #${PRINT_HEADER_ID} #document-info-header img {
      height: 3rem !important;
      width: auto !important;
    }
  </style>
`;

const buildPrintContent = (headerHtml: string, bodyHtml: string, repeatHeaderOnEveryPage: boolean) => {
  const header = `<div id="${PRINT_HEADER_ID}">${headerHtml}</div>`;
  const body = `<div id="${PRINT_BODY_ID}">${bodyHtml}</div>`;

  if (!repeatHeaderOnEveryPage) {
    return `${header}${body}`;
  }

  return `
    <table style="width: 100%; border-collapse: collapse;">
      <thead style="display: table-header-group;">
        <tr><td style="padding: 0;">${header}</td></tr>
      </thead>
      <tbody>
        <tr><td style="padding: 0;">${body}</td></tr>
      </tbody>
    </table>
  `;
};

export const buildModalPrintDocument = ({
  title,
  headerHtml,
  bodyHtml,
  styles,
  compact = false,
  repeatHeaderOnEveryPage = false,
}: BuildModalPrintDocumentParams) => `
  <!DOCTYPE html>
  <html>
    <head>
      <title>${escapeHtml(title)}</title>
      <style>@page { size: A4 portrait; margin: ${PRINT_PAGE_MARGIN_MM}mm; }</style>
      ${styles}
      <style>
        body {
          display: block !important;
          margin: 0 !important;
          width: ${PRINT_PAGE_CONTENT_WIDTH_MM}mm !important;
          min-width: 0 !important;
          min-height: 0 !important;
        }
        #${PRINT_HEADER_ID},
        #${PRINT_BODY_ID},
        #${PRINT_HEADER_ID} > *,
        #${PRINT_BODY_ID} > *,
        #${PRINT_BODY_ID} > * > * {
          width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
      </style>
      ${compact ? COMPACT_PRINT_STYLE : ''}
      ${repeatHeaderOnEveryPage ? REPEATED_PRINT_HEADER_STYLE : ''}
    </head>
    <body class="!block !m-0 !min-h-0 !min-w-0 !box-border !bg-white-01 !p-6 text-gray-07">
      ${buildPrintContent(headerHtml, bodyHtml, repeatHeaderOnEveryPage)}
    </body>
  </html>
`;

const isHiddenInPrint = (element: Element) => element.classList.contains('print:hidden');

export const flattenPrintRowElements = (elements: Element[]): Element[] =>
  elements.flatMap((element) =>
    element.tagName === 'DIV' && element.children.length > 1 ? Array.from(element.children) : [element]
  );

export const syncFormFieldValues = (root: Element) => {
  root.querySelectorAll('input').forEach((input) => {
    if (input.type === 'checkbox' || input.type === 'radio') {
      input.toggleAttribute('checked', input.checked);
      return;
    }

    input.setAttribute('value', input.value);
  });

  root.querySelectorAll('textarea').forEach((textarea) => {
    textarea.textContent = textarea.value;
  });

  root.querySelectorAll('select').forEach((select) => {
    Array.from(select.options).forEach((option) => option.toggleAttribute('selected', option.selected));
  });
};

export const PRINT_LOGO_ATTRIBUTE = 'data-print-logo';

/**
 * The on-screen logo points at an authenticated stream that the print window loads
 * with whatever session cookie is left, outside the api client that refreshes it.
 * A caller that resolved the logo through the api swaps it in on a copy of the header.
 */
export const buildPrintHeaderHtml = (headerElement: Element | undefined, logoSrc?: string) => {
  if (!headerElement) {
    return '';
  }

  if (logoSrc === undefined) {
    return headerElement.outerHTML;
  }

  const printableHeader = headerElement.cloneNode(true) as Element;
  printableHeader
    .querySelectorAll(`img[${PRINT_LOGO_ATTRIBUTE}]`)
    .forEach((logo) => logo.setAttribute('src', logoSrc));

  return printableHeader.outerHTML;
};

/**
 * Anything marked as hidden in print is left out of the document instead of being
 * carried over and hidden by CSS: the flattening above breaks a row with more than
 * one child apart, which would drop the `print:hidden` of the row and print the
 * controls it wraps. Filtering on both sides of the flattening keeps a hidden row
 * out whole and a hidden child of a visible row out on its own.
 */
export const buildPrintBodyHtml = (elements: Element[]) =>
  flattenPrintRowElements(elements.filter((element) => !isHiddenInPrint(element)))
    .filter((element) => !isHiddenInPrint(element))
    .map(
      (element) =>
        `<div style="break-inside: avoid; page-break-inside: avoid;">${element.outerHTML}</div>`
    )
    .join('');

const waitForPrintImage = (image: HTMLImageElement) => {
  if (image.complete && image.naturalWidth > 0) {
    return Promise.resolve();
  }

  if (typeof image.decode === 'function') {
    return image.decode().catch(() => undefined);
  }

  return new Promise<void>((resolve) => {
    image.onload = () => resolve();
    image.onerror = () => resolve();
  });
};

const waitForPrintStylesheet = (link: HTMLLinkElement) => {
  if (link.sheet) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    link.onload = () => resolve();
    link.onerror = () => resolve();
  });
};

export const waitForPrintAssets = async (printWindow: Window) => {
  const images = Array.from(printWindow.document.querySelectorAll<HTMLImageElement>('img'));
  const stylesheets = Array.from(
    printWindow.document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
  );
  const fontsReady = printWindow.document.fonts?.ready ?? Promise.resolve();

  await Promise.all([fontsReady, ...images.map(waitForPrintImage), ...stylesheets.map(waitForPrintStylesheet)]);
};

export const ensurePrintSvgViewBox = (printWindow: Window) => {
  const svgs = Array.from(printWindow.document.querySelectorAll<SVGSVGElement>('svg'));

  svgs.forEach((svg) => {
    if (svg.hasAttribute('viewBox')) return;

    const width = Number.parseFloat(svg.getAttribute('width') ?? '');
    const height = Number.parseFloat(svg.getAttribute('height') ?? '');
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  });
};

export const waitForPrintLayoutSettle = (printWindow: Window) =>
  new Promise<void>((resolve) => {
    printWindow.requestAnimationFrame(() => {
      printWindow.requestAnimationFrame(() => resolve());
    });
  });

export const repeatPrintHeaderAcrossPages = (
  printWindow: Window,
  pageContentHeightPx: number = PRINT_PAGE_CONTENT_HEIGHT_PX
) => {
  const doc = printWindow.document;
  const header = doc.getElementById(PRINT_HEADER_ID);
  const body = doc.getElementById(PRINT_BODY_ID);
  if (!header || !body) return;

  const headerHeight = header.getBoundingClientRect().height;
  let runningHeight = headerHeight;

  Array.from(body.children).forEach((row) => {
    const rowHeight = row.getBoundingClientRect().height;

    if (rowHeight > pageContentHeightPx) {
      runningHeight = (runningHeight + rowHeight) % pageContentHeightPx;
      return;
    }

    if (runningHeight > headerHeight && runningHeight + rowHeight > pageContentHeightPx) {
      const headerClone = header.cloneNode(true) as HTMLElement;
      headerClone.removeAttribute('id');
      headerClone.style.breakBefore = 'page';
      headerClone.style.pageBreakBefore = 'always';
      body.insertBefore(headerClone, row);
      runningHeight = headerHeight + rowHeight;
    } else {
      runningHeight += rowHeight;
    }
  });
};

// The print window carries no .dark class and no inline branch colors, so it needs the raw
// branch color: color-modes.css derives --theme-secondary from it in light mode there.
const collectBrandSecondaryStyle = () => {
  const brandSecondary = document.documentElement.style.getPropertyValue('--brand-secondary').trim();

  return brandSecondary ? `<style>:root { --brand-secondary: ${brandSecondary}; }</style>` : '';
};

export const collectPrintStyles = () =>
  [
    ...Array.from(document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style')).map(
      (element) => {
        if (element instanceof HTMLLinkElement) {
          return `<link rel="stylesheet" href="${element.href}">`;
        }

        return element.outerHTML;
      }
    ),
    collectBrandSecondaryStyle(),
  ]
    .filter(Boolean)
    .join('\n');
